import {
  BackButton,
  Button,
  Flex,
  IconWrap,
  InformationCircleBoldIcon,
  Loader,
  Modal,
  Title,
  Tooltip,
  Table,
  createAppColumnHelper,
  NumberInput
} from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import REST_MAP, { CUSTOM } from '@spa/constants/RestTypes'
import {
  updateStep,
  Step
} from '@spa/scenes/Plannings/Modals/RestModal/redux/modules/main'
import { formatToWeekISO, roundToTwoDecimals } from '@spa/services/utils'
import moment from 'moment'
import type { FC } from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import { RestSummary } from '../RestForm/RestFormSection/components'
import { InfoUpdateRestDuration } from '../RestForm/RestFormSection/components/Alerts'

import styles from './RevaluationTableForm.module.css'

export const NB_HOURS_IN_WEEK_MAX = 168

export const getSplitedHeader = string => (
  <span className={styles.Value}>{string.replace(/ /g, '\n')}</span>
)

export type Props = {
  handleRevaluatedInputDataChange: Function
  isLoading: boolean
  manualValuationData: any[]
  formData: {
    starts_at: { value: string }
    start_half_day: { value: string }
    ends_at: { value: string }
    end_half_day: { value: string }
  }
  restIsLocked: boolean
  restType: string
  valuatedInputData: object
  setRestOption: (opts: any) => void
  isCustomized: boolean
}

type RevaluationTableColumns = {
  week: string
  contract_time: number
  worked_hours: number
  other_rests: number
  valuated_data: number
  diff_contract: number
  is_complete_week: boolean
}

export const RevaluationTableForm: FC<Props> = ({
  handleRevaluatedInputDataChange,
  isLoading,
  manualValuationData,
  formData,
  restIsLocked,
  restType,
  valuatedInputData,
  setRestOption,
  isCustomized
}) => {
  const { t, tDates, tCommon } = usePlanningFormTranslation()
  const dispatch = useDispatch()

  const hasUncompleteWeeks = manualValuationData.some(
    week => !week.is_complete_week
  )
  const getOtherRestsDuration = weekData => {
    const week = `${formatToWeekISO(weekData)}`
    return valuatedInputData[week]?.otherRestsDuration || 0
  }

  const methods = useForm({
    mode: 'onBlur'
  })

  // Reset customized hours to default values
  useEffect(() => {
    if (!isCustomized) {
      const weeks = Object.keys(valuatedInputData)
      weeks.map(week => methods.setValue(week, valuatedInputData[week].value))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomized, valuatedInputData])

  const onSubmit = (values, e) => {
    e.preventDefault()

    handleRevaluatedInputDataChange(values)

    setRestOption(CUSTOM)
    dispatch(updateStep(Step.FORM))
  }

  const onCancelModal = () => {
    dispatch(updateStep(Step.FORM))
  }

  const renderDiff = useCallback(
    ({
      week,
      is_complete_week: isCompleteWeek,
      contract_time: contractTime,
      worked_hours: workedHours
    }) => {
      const weekIso = formatToWeekISO(week)
      const inputValue = Number(methods.watch()?.[weekIso]) || 0
      const otherRestsDurationValue = getOtherRestsDuration(week)
      const diff = isCompleteWeek
        ? 0
        : roundToTwoDecimals(workedHours) +
          roundToTwoDecimals(otherRestsDurationValue) +
          inputValue -
          contractTime

      return (
        <span className={styles.Value}>
          {diff > 0 ? '+' : ''}
          {tDates('duration.hourDecimalToHours', { duration: diff || 0 })}
        </span>
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methods.watch()]
  )

  const minimumRestHours = restType => {
    const minMinutes =
      REST_MAP.get(restType)?.revaluation_hours_in_week_min || 0
    if (minMinutes === 0) {
      return 0
    }

    /*
     * We need to round the value to 1 decimal because the
     * <input> field is buggy when the min attribute is on 2 decimals
     */
    const minHours = minMinutes / 60
    return minHours < 0.1 ? 0.1 : parseFloat(minHours.toFixed(1))
  }

  const columnHelper = createAppColumnHelper<RevaluationTableColumns>()

  const columns = useMemo(
    () => [
      columnHelper.display({
        header: t('restForm.week'),
        id: 'week',
        meta: {
          className: '!p-0',
          headerCellClassName: '!rounded-l-none'
        },
        cell: ({
          row: {
            original: { week }
          }
        }) => (
          <span className='text-xs'>
            {tDates('range.weekIndexAndDayShortMonth', {
              start: moment(week).startOf('isoWeek'),
              end: moment(week).endOf('isoWeek')
            })}
          </span>
        ),
        size: 85
      }),
      columnHelper.display({
        header: t('restForm.contract'),
        id: 'contract_time',
        meta: {
          className: '!p-1',
          headerCellClassName: '!p-1'
        },
        cell: ({
          row: {
            original: { contract_time }
          }
        }) => <span className={styles.Value}>{contract_time}h</span>,
        size: 65
      }),
      columnHelper.display({
        header: () => getSplitedHeader(t('restForm.workedHours')),
        id: 'worked_hours',
        cell: ({
          row: {
            original: { worked_hours }
          }
        }) => (
          <span className={styles.Value}>
            {tDates('duration.hourDecimalToHours', { duration: worked_hours })}
          </span>
        ),
        meta: {
          className: '!p-1',
          headerCellClassName: '!p-1'
        },
        size: 65
      }),
      columnHelper.display({
        header: () => getSplitedHeader(t('restForm.otherRests')),
        id: 'other_rests',
        cell: ({
          row: {
            original: { week }
          }
        }) => (
          <span className={styles.Value}>
            {tDates('duration.hourDecimalToHours', {
              duration: getOtherRestsDuration(week)
            })}
          </span>
        ),
        meta: {
          className: '!p-1',
          headerCellClassName: '!p-1'
        },
        size: 65
      }),
      columnHelper.display({
        header: () => (
          <Tooltip
            label={t('restForm.valuatedHoursTooltip')}
            id='valuated_hours'
            position='top'
          >
            <Flex className='items-center gap-1'>
              {getSplitedHeader(t('restForm.upgradedHours'))}
              <IconWrap icon={<InformationCircleBoldIcon />} size='s' />
            </Flex>
          </Tooltip>
        ),
        id: 'valuated_data',
        meta: {
          className: '!p-1',
          headerCellClassName: '!p-1'
        },
        cell: ({
          row: {
            original: { is_complete_week, contract_time, week }
          }
        }) =>
          is_complete_week ? (
            <span className={styles.Value}>{contract_time}h</span>
          ) : (
            <Controller
              name={formatToWeekISO(week)}
              control={methods.control}
              defaultValue={valuatedInputData[formatToWeekISO(week)]?.value}
              render={({ field }) => (
                <NumberInput
                  id={formatToWeekISO(week)}
                  step='0.1'
                  min={minimumRestHours(restType)}
                  max={NB_HOURS_IN_WEEK_MAX}
                  disabled={restIsLocked}
                  {...field}
                />
              )}
            />
          ),
        size: 90
      }),
      columnHelper.display({
        header: () => getSplitedHeader(t('restForm.contractDifference')),
        id: 'diff_contract',
        meta: {
          className: '!p-1',
          headerCellClassName: '!rounded-r-none !p-1'
        },
        cell: ({
          row: {
            original: { is_complete_week, contract_time, week, worked_hours }
          }
        }) =>
          renderDiff({ is_complete_week, contract_time, week, worked_hours }),
        size: 65
      })
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [manualValuationData, valuatedInputData]
  )

  return (
    <Modal.Content>
      <Title size={4}>{t('restForm.tableTitle')}</Title>
      <BackButton
        size='s'
        onClick={onCancelModal}
        data-testid='button-back'
        className='my-4'
      >
        {tCommon('action.back')}
      </BackButton>
      <form>
        <RestSummary restType={restType} {...formData} />
        {isLoading && <Loader />}
        {!isLoading && (
          <div className={styles.TableContainer}>
            <Table
              columns={columns}
              data={manualValuationData}
              cellsPaddingSize='s'
              cellsContent='auto'
            />
          </div>
        )}
        {hasUncompleteWeeks && (
          <div className={styles.SubmitContainer}>
            <Button
              variant='primary'
              intent='default'
              onClick={methods.handleSubmit(onSubmit)}
            >
              {t('restForm.validate')}
            </Button>
          </div>
        )}
        <InfoUpdateRestDuration />
      </form>
    </Modal.Content>
  )
}
